<template>
  <div class="player-canvas-container" @click="closeContextMenu">
    <!-- Action Holder Panel (Skills & Items) -->
    <div v-if="selectedPlayer" class="rpg-action-holder">
      <ActionHolder
        :skills="playerSkills"
        :items="playerItems"
        :specials="[]"
        @skill-selected="handleSkillSelected"
        @item-selected="handleItemSelected"
      />
    </div>

    <canvas
      ref="canvas"
      class="player-canvas"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseLeave"
      @wheel.prevent="handleWheel"
      @click="handleCanvasClick"
    ></canvas>

    <!-- Impacts land on the floor the tokens stand on: same contract as the player
         board's own effects layer - sized to the container, never CSS-scaled by the
         zoom. The zoom is folded into the four grid corners handed to the shader
         instead, through `applyMorph`'s own bilinear map, so a cell drawn here and a
         cell painted by this canvas's 2D context are one function of one set of
         numbers. No fog on the GM board: the GM sees every cell the server resolved. -->
    <canvas ref="effectsCanvas" class="impact-stage"></canvas>

    <!-- Character Tokens Overlay -->
    <div class="tokens-overlay" :style="{ transform: `scale(${zoom})`, transformOrigin: 'center center' }">
      <div
        v-for="player in players"
        :key="player.id"
        class="token-wrapper"
        :class="{
          'token-selected': selectedPlayer && selectedPlayer.id === player.id,
          'token-no-pointer': selectedSkill !== null
        }"
        :style="getTokenStyle(player)"
        @click="handleTokenClick(player.id)"
        @contextmenu.prevent="handleTokenContextMenu($event, player)"
      >
        <CharacterToken
          :image="player.image"
          :knocked-out="isPlayerKnockedOut(player)"
          :signed="player.game_master_mode"
        />
        <div class="token-name">{{ player.name }}</div>
      </div>
    </div>

    <!-- Item/Object Tokens Overlay (smaller than character tokens) -->
    <div class="tokens-overlay" :style="{ transform: `scale(${zoom})`, transformOrigin: 'center center' }">
      <div
        v-for="item in mapItems"
        :key="'item-' + item.id"
        class="token-wrapper item-token"
        :style="getItemTokenStyle(item)"
        @click="handleItemTokenClick(item.id)"
        @contextmenu.prevent="handleItemTokenContextMenu($event, item)"
      >
        <ObjectToken
          :image="item.image"
        />
        <div class="token-name item-name">{{ item.name }}</div>
      </div>
    </div>

    <!-- Coordinate Display -->
    <div v-if="hoveredCell" class="coordinate-display">
      <span class="coord-label">Position:</span>
      <span class="coord-value">X: {{ hoveredCell.x }}, Y: {{ hoveredCell.y }}</span>
      <span class="coord-layer">Layer: {{ hoveredCell.layer }}</span>
      <span v-if="selectedSkill && skillTargetDistance !== null" class="coord-distance">
        Distance: {{ skillTargetDistance }}
        <span v-if="selectedSkill.min_range || selectedSkill.max_range" class="range-indicator">
          ({{ selectedSkill.min_range || 0 }}-{{ selectedSkill.max_range || '∞' }})
        </span>
      </span>
    </div>

    <!-- Zoom HUD -->
    <div class="zoom-hud">
      <button class="zoom-btn" @click="zoomOut" :disabled="zoom <= minZoom" title="Zoom Out">−</button>
      <span class="zoom-display">{{ zoomPercentage }}%</span>
      <button class="zoom-btn" @click="zoomIn" :disabled="zoom >= maxZoom" title="Zoom In">+</button>
      <button class="zoom-btn zoom-reset" @click="resetZoom" title="Reset Zoom">⟲</button>
    </div>

    <!-- Skill Rotation Control -->
    <div v-if="selectedSkill && selectedSkill.spray_config && selectedSkill.spray_config.rotatable" class="rotation-control">
      <button @click="rotateSkillPattern(-90)" class="rotation-btn" title="Rotate Left">↶</button>
      <span class="rotation-display">{{ skillRotation }}°</span>
      <button @click="rotateSkillPattern(90)" class="rotation-btn" title="Rotate Right">↷</button>
    </div>

    <!-- Character Context Menu -->
    <CharacterContextMenu
      :visible="contextMenuVisible"
      :character="contextMenuCharacter"
      :position="contextMenuPosition"
      :show-stats="true"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { TERRAIN_CONFIGS } from '@/types/tabletop';
import CharacterTokenV2 from '@/components/CharacterToken/CharacterTokenV2.vue';
import ObjectToken from '@/components/CharacterToken/ObjectToken.vue';
import CharacterContextMenu from '@/components/CharacterContextMenu/CharacterContextMenu.vue';
import ActionHolder from '@/components/ActionArea/ActionHolder/ActionHolder.vue';
import { ImpactBoardLayer, boardProjection } from '@/services/impact/boardLayer';
import { playScheduled } from '@/services/impact/schedule';
import { buildReel, normalizeGmAction } from '@/services/impact/reel';

/* A GM session runs for hours and nothing prunes for us, so both the feed and the
   memory of what has been drawn are rolling windows rather than histories. */
const FEED_WINDOW = 200;
const PLAYED_WINDOW = 400;

export default {
  name: 'PlayerCanvas',
  components: {
    CharacterToken: CharacterTokenV2,
    ObjectToken,
    CharacterContextMenu,
    ActionHolder
  },
  data() {
    return {
      canvas: null,
      ctx: null,
      hoveredCell: null,
      canvasWidth: 0,
      canvasHeight: 0,
      offsetX: 0,
      offsetY: 0,
      backgroundImage: null,
      // Pathfinding cache
      reachableCells: new Map(), // Map of "x,y,layer" -> { x, y, layer, cost, path }
      pathToHovered: null,
      // Animation state
      isAnimating: false,
      animationPlayerId: null,
      animationPath: null,
      animationCurrentStep: 0,
      animationProgress: 0, // 0 to 1, progress within current step
      // Pan state
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      panStartOffsetX: 0,
      panStartOffsetY: 0,
      // Zoom state
      zoom: 1.0,
      minZoom: 0.5,
      maxZoom: 3.0,
      zoomStep: 1.1, // Exponential zoom factor
      // Context menu state
      contextMenuVisible: false,
      contextMenuCharacter: null,
      contextMenuPosition: { x: 0, y: 0 },
      // Skill targeting state
      selectedSkill: null,
      skillRotation: 0, // 0, 90, 180, 270
      skillTargetPosition: null, // { x, y } grid coordinates
      skillAffectedCells: [], // Array of { x, y, valid } objects
      skillTargetDistance: null,
      // Character skills and items for ActionHolder
      playerSkills: [],
      playerItems: []
    };
  },
  computed: {
    ...mapGetters('tabletopPlayer', [
      'currentMap',
      'players',
      'selectedPlayer',
      'currentLayer',
      'gridVisible',
      'backgroundVisible',
      'gridConfig',
      'getPlayerByCharacterId'
    ]),
    zoomPercentage() {
      return Math.round(this.zoom * 100);
    },
    // Items on the map (can be actual game items, loot, interactive objects, etc.)
    mapItems() {
      // TODO: This should come from the Vuex store when items are implemented
      // For now, return empty array or items from currentMap if they exist
      return this.currentMap?.items || [];
    },
    // Scaled grid config - calculates actual cell sizes based on current image dimensions
    // This ensures grid scales proportionally with the image across different resolutions
    scaledGridConfig() {
      if (!this.gridConfig) return null;

      // If no reference dimensions are set, or no background image, use original values
      if (!this.gridConfig.referenceImageWidth || !this.gridConfig.referenceImageHeight || !this.backgroundImage) {
        return this.gridConfig;
      }

      // Calculate scale factors based on current image size vs reference size
      const scaleX = this.backgroundImage.width / this.gridConfig.referenceImageWidth;
      const scaleY = this.backgroundImage.height / this.gridConfig.referenceImageHeight;

      // Return scaled grid config
      return {
        ...this.gridConfig,
        cellWidth: this.gridConfig.cellWidth * scaleX,
        cellHeight: this.gridConfig.cellHeight * scaleY
      };
    }
  },
  watch: {
    currentMap: {
      handler() {
        this.loadBackgroundImage();
        this.render();
      },
      deep: true
    },
    players: {
      handler() {
        this.render();
      },
      deep: true
    },
    selectedPlayer: {
      handler(newPlayer) {
        this.calculateReachableCells();
        this.loadPlayerSkillsAndItems(newPlayer);
        this.render();
      },
      deep: true
    },
    gridVisible() {
      this.render();
    },
    backgroundVisible() {
      this.render();
    }
  },
  created() {
    // Off `data` for the same reason the player board keeps its stage there: a
    // three.js scene graph behind Vue's reactive proxy throws on every frame.
    this.impactLayer = null;
    this.bus = null;
    this.actionFeed = []; // This board's own copy of the GM action feed - it is not nested under GameMasterMain, so it keeps none of that view's state
    this.playedActionIds = new Set(); // Actions already drawn, so a repeated event cannot replay them
    this.reelRunning = null; // The reel in flight, so an overlapping event queues instead of racing
  },
  async mounted() {
    this.initCanvas();
    this.loadBackgroundImage();
    this.render();
    window.addEventListener('resize', this.handleResize);
    await this.subscribeToActionFeed();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.unsubscribeFromActionFeed();
    if (this.impactLayer) {
      this.impactLayer.dispose();
      this.impactLayer = null;
    }
  },
  methods: {
    ...mapActions('tabletopPlayer', [
      'selectPlayer',
      'movePlayer'
    ]),

    initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext('2d');

      this.impactLayer = new ImpactBoardLayer(this.$refs.effectsCanvas, {
        projection: () => this.impactProjection(),
      });

      this.handleResize();
    },

    handleResize() {
      const container = this.canvas.parentElement;
      this.canvasWidth = container.clientWidth;
      this.canvasHeight = container.clientHeight;
      this.canvas.width = this.canvasWidth;
      this.canvas.height = this.canvasHeight;
      this.impactLayer?.resize(this.canvasWidth, this.canvasHeight);
      this.render();
    },

    // Zoom methods
    handleWheel(event) {
      const delta = event.deltaY;
      if (delta < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    },

    zoomIn() {
      this.setZoom(this.zoom * this.zoomStep);
    },

    zoomOut() {
      this.setZoom(this.zoom / this.zoomStep);
    },

    resetZoom() {
      this.setZoom(1.0);
    },

    setZoom(newZoom) {
      // Clamp zoom to min/max range
      this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));
      this.render();
    },

    loadBackgroundImage() {
      if (this.currentMap?.backgroundImage) {
        const img = new Image();
        img.onload = () => {
          this.backgroundImage = img;
          this.render();
        };
        img.src = this.currentMap.backgroundImage;
      } else {
        this.backgroundImage = null;
        this.render();
      }
    },

    // Dijkstra pathfinding to calculate all reachable cells from selected player
    calculateReachableCells() {
      this.reachableCells.clear();

      const scaledConfig = this.scaledGridConfig;
      if (!this.selectedPlayer || !this.currentMap || !scaledConfig) {
        console.log('[Calculate] Early return - selectedPlayer:', !!this.selectedPlayer, 'currentMap:', !!this.currentMap, 'scaledGridConfig:', !!scaledConfig);
        return;
      }

      const startX = this.selectedPlayer.x;
      const startY = this.selectedPlayer.y;
      const startLayer = this.selectedPlayer.layer;
      const maxCost = this.selectedPlayer.currentActionPoints;

      console.log(`[Calculate] Starting pathfinding from (${startX}, ${startY}, layer ${startLayer}) with ${maxCost} action points`);
      console.log('[Calculate] Map has', this.currentMap.edges?.length || 0, 'edges total');

      // Priority queue: [cost, x, y, layer, path]
      const queue = [[0, startX, startY, startLayer, []]];
      const visited = new Set();

      while (queue.length > 0) {
        // Sort by cost (simple priority queue)
        queue.sort((a, b) => a[0] - b[0]);
        const [cost, x, y, layer, path] = queue.shift();

        const key = `${x},${y},${layer}`;
        if (visited.has(key)) continue;
        visited.add(key);

        // Store reachable cell with its cost and path
        this.reachableCells.set(key, { x, y, layer, cost, path: [...path, { x, y, layer }] });

        // Don't expand if we've reached max cost
        if (cost >= maxCost) continue;

        // Get all valid edges from this cell
        const edges = this.getEdgesFromCell(x, y, layer);

        edges.forEach(edge => {
          const toKey = `${edge.toX},${edge.toY},${edge.toLayer}`;
          if (visited.has(toKey)) return;

          // Calculate movement cost
          const cellCost = this.getCellMovementCost(edge.toX, edge.toY, edge.toLayer);
          const newCost = cost + cellCost;

          if (newCost <= maxCost) {
            queue.push([newCost, edge.toX, edge.toY, edge.toLayer, [...path, { x, y, layer }]]);
          }
        });
      }

      console.log(`[Calculate] ✅ Pathfinding complete! Found ${this.reachableCells.size} reachable cells`);
      if (this.reachableCells.size <= 1) {
        console.warn('[Calculate] ⚠️ Only starting cell found - no movement possible!');
      }
    },

    // Get all valid (non-blocked) edges from a cell
    getEdgesFromCell(x, y, layer) {
      if (!this.currentMap || !this.currentMap.edges) {
        console.log(`[GetEdges] No map or edges array for (${x}, ${y}, ${layer})`);
        return [];
      }

      const edges = this.currentMap.edges.filter(edge =>
        edge && edge.id &&
        edge.id.fromX === x &&
        edge.id.fromY === y &&
        edge.id.layer === layer &&
        !edge.blocked
      ).map(edge => ({
        toX: edge.id.toX,
        toY: edge.id.toY,
        toLayer: edge.id.layer
      }));

      console.log(`[GetEdges] From (${x}, ${y}, ${layer}): found ${edges.length} edges`);
      return edges;
    },

    // Get movement cost for a cell based on terrain
    getCellMovementCost(x, y, layer) {
      if (!this.currentMap || !this.currentMap.cells) return 1;

      const cell = this.currentMap.cells.find(c =>
        c.x === x && c.y === y && c.layer === layer
      );

      if (!cell) return 1; // Default grass cost

      const terrainConfig = TERRAIN_CONFIGS[cell.terrain];
      return terrainConfig ? terrainConfig.movementCost : 1;
    },

    render() {
      if (!this.ctx || !this.currentMap) return;

      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Save context state and apply zoom transformation
      this.ctx.save();

      // Translate to center, apply zoom, translate back
      this.ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
      this.ctx.scale(this.zoom, this.zoom);
      this.ctx.translate(-this.canvasWidth / 2, -this.canvasHeight / 2);

      // Draw background
      if (this.backgroundVisible && this.backgroundImage) {
        this.drawBackground();
      } else {
        this.ctx.fillStyle = '#1a1a1a';
        this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
      }

      // Draw movement area shadows for selected player (only if grid is visible and no skill selected)
      if (this.selectedPlayer && this.gridVisible && !this.selectedSkill) {
        this.drawMovementArea();
      }

      // Draw grid (only for reachable cells if player is selected)
      if (this.gridVisible && this.scaledGridConfig) {
        this.drawGrid();
      }

      // Draw skill preview if skill is selected
      if (this.selectedSkill && this.selectedPlayer && this.skillTargetPosition) {
        this.drawSkillPreview();
      }

      // Draw path to hovered cell (only if grid is visible and no skill selected)
      if (this.pathToHovered && this.pathToHovered.length > 0 && this.gridVisible && !this.selectedSkill) {
        this.drawPath(this.pathToHovered);
      }

      // Draw hovered cell shadow (only if grid is visible and cell is passable and no skill selected)
      if (this.hoveredCell && this.gridVisible && this.isCellPassable(this.hoveredCell.x, this.hoveredCell.y) && !this.selectedSkill) {
        this.drawCellShadow(this.hoveredCell.x, this.hoveredCell.y);
      }

      // Restore context state
      this.ctx.restore();

      // Players are now rendered as DOM elements via ObjectToken components
      // (see tokens-overlay in template)
    },

    // Calculate the position where the image is drawn in the canvas
    // This is the reference point for all grid and cell positioning
    getImagePosition() {
      if (!this.backgroundImage) {
        return { x: 0, y: 0, width: 0, height: 0 };
      }

      const width = this.backgroundImage.width;
      const height = this.backgroundImage.height;

      // Center the image in the canvas viewport, with pan offset
      const x = (this.canvasWidth - width) / 2 + this.offsetX;
      const y = (this.canvasHeight - height) / 2 + this.offsetY;

      return { x, y, width, height };
    },

    /**
     * The same geometry `applyMorph` draws with, for the effects layer.
     *
     * Read fresh on every frame the layer draws, exactly as the player board does it:
     * pan, zoom and `gridConfig` change from a dozen places on this board too, and
     * none of them announce themselves.
     */
    impactProjection() {
      if (!this.gridConfig) return null;
      const imagePos = this.getImagePosition();
      return boardProjection({
        gridSettings: this.gridConfig,
        image: this.backgroundImage,
        origin: { x: imagePos.x, y: imagePos.y },
        zoom: this.zoom,
        viewport: { width: this.canvasWidth, height: this.canvasHeight },
      });
    },

    /** No fog on this board - the GM sees every cell the server resolved. */
    playImpactCast(cast, hooks = {}) {
      if (!this.impactLayer || !cast) return Promise.resolve(null);
      return this.impactLayer.playCast(cast, hooks);
    },

    /** Where a spawned token stands right now, keyed by the backend character id the reel carries. */
    tokenCellOf(characterId) {
      if (!characterId) return null;
      const player = this.getPlayerByCharacterId(characterId);
      if (!player) return null;
      return { col: player.x, row: player.y, id: characterId };
    },

    /**
     * `world::action_accepted` / `world::action_performed` land on the master channel,
     * which this board is not a nested consumer of `GameMasterMain.vue`'s feed - so it
     * keeps its own tiny copy of the same shape, updated by id exactly as that view
     * does, and hands it to `buildReel` unchanged.
     */
    async subscribeToActionFeed() {
      const { ensureConnection } = await import('@/api/dx-websocket/index.ts');
      this.bus = ensureConnection();
      this.bus.on('world::action_accepted', this.handleGmAction);
      this.bus.on('world::action_performed', this.handleGmAction);
    },

    unsubscribeFromActionFeed() {
      if (!this.bus) return;
      this.bus.off('world::action_accepted', this.handleGmAction);
      this.bus.off('world::action_performed', this.handleGmAction);
    },

    /**
     * The GM feed, in the shape the reel speaks.
     *
     * `reel.js` owns that translation (`normalizeGmAction`) rather than this board,
     * so the rule about which nested fields get flattened lives next to the code that
     * depends on it — a field added to what the reel reads is then one list to update,
     * not a component someone has to remember exists.
     *
     * The feed is a WINDOW, not a history: this board holds its own copy (it is not a
     * nested consumer of `GameMasterMain.vue`'s), a GM session runs for hours, and
     * nothing here ever re-fetches a bounded page the way the player board does. Only
     * what is recent enough to still be worth drawing is kept.
     */
    handleGmAction(action) {
      if (!action?.id) return;
      const normalized = normalizeGmAction(action);
      const index = this.actionFeed.findIndex(entry => entry.id === normalized.id);
      if (index !== -1) this.actionFeed[index] = normalized;
      else this.actionFeed.push(normalized);
      if (this.actionFeed.length > FEED_WINDOW) this.actionFeed.splice(0, this.actionFeed.length - FEED_WINDOW);
      this.playReel();
    },

    /** What is new in the feed, drawn in order - the same differ the player board uses. */
    playReel() {
      if (!this.impactLayer) return;
      const { casts, actionIds } = buildReel(this.actionFeed, this.playedActionIds, {
        positionOf: id => this.tokenCellOf(id),
      });
      actionIds.forEach(id => this.playedActionIds.add(id));
      /* The memory only has to outlive the feed it diffs against; an id no window
         still holds can never be offered a second time. */
      if (this.playedActionIds.size > PLAYED_WINDOW) {
        [...this.playedActionIds].slice(0, this.playedActionIds.size - PLAYED_WINDOW)
          .forEach(id => this.playedActionIds.delete(id));
      }
      if (!casts.length) return;
      this.queueCasts(casts);
    },

    queueCasts(casts) {
      this.reelRunning = playScheduled(casts, cast => this.playImpactCast(cast), this.reelRunning);
      return this.reelRunning;
    },

    drawBackground() {
      if (!this.backgroundImage) return;

      // Render image at its native size, independent of canvas size
      // The canvas acts as a viewport/camera showing part of the image
      const imagePos = this.getImagePosition();

      this.ctx.globalAlpha = 0.5;
      this.ctx.drawImage(this.backgroundImage, imagePos.x, imagePos.y, imagePos.width, imagePos.height);
      this.ctx.globalAlpha = 1.0;
    },

    applyMorph(x, y, gridWidth, gridHeight, startX, startY) {
      const { xMorph, yMorph, cornerOffsets } = this.gridConfig;

      // Use 4-point morphing if cornerOffsets are defined
      if (cornerOffsets) {
        // Calculate normalized position within the grid (0 to 1)
        const u = (x - startX) / gridWidth;
        const v = (y - startY) / gridHeight;

        // Define the four corners of the grid in screen space
        // Default positions (no morph)
        const topLeftDefault = { x: startX, y: startY };
        const topRightDefault = { x: startX + gridWidth, y: startY };
        const bottomLeftDefault = { x: startX, y: startY + gridHeight };
        const bottomRightDefault = { x: startX + gridWidth, y: startY + gridHeight };

        // Apply corner offsets (scaled by grid dimensions)
        const topLeft = {
          x: topLeftDefault.x + cornerOffsets.topLeft.x * gridWidth,
          y: topLeftDefault.y + cornerOffsets.topLeft.y * gridHeight
        };
        const topRight = {
          x: topRightDefault.x + cornerOffsets.topRight.x * gridWidth,
          y: topRightDefault.y + cornerOffsets.topRight.y * gridHeight
        };
        const bottomLeft = {
          x: bottomLeftDefault.x + cornerOffsets.bottomLeft.x * gridWidth,
          y: bottomLeftDefault.y + cornerOffsets.bottomLeft.y * gridHeight
        };
        const bottomRight = {
          x: bottomRightDefault.x + cornerOffsets.bottomRight.x * gridWidth,
          y: bottomRightDefault.y + cornerOffsets.bottomRight.y * gridHeight
        };

        // Bilinear interpolation
        // Interpolate top edge
        const topX = topLeft.x * (1 - u) + topRight.x * u;
        const topY = topLeft.y * (1 - u) + topRight.y * u;

        // Interpolate bottom edge
        const bottomX = bottomLeft.x * (1 - u) + bottomRight.x * u;
        const bottomY = bottomLeft.y * (1 - u) + bottomRight.y * u;

        // Interpolate between top and bottom
        const finalX = topX * (1 - v) + bottomX * v;
        const finalY = topY * (1 - v) + bottomY * v;

        return {
          x: finalX,
          y: finalY
        };
      }

      // Legacy behavior: simple axis-based morphing
      const relX = (x - startX) / gridWidth;
      const relY = (y - startY) / gridHeight;

      const xOffset = xMorph * gridWidth * 0.3 * (relY - 0.5);
      const yOffset = yMorph * gridHeight * 0.3 * (relX - 0.5);

      return {
        x: x + xOffset,
        y: y + yOffset
      };
    },

    inverseMorph(screenX, screenY, gridWidth, gridHeight, startX, startY) {
      const { xMorph, yMorph, cornerOffsets } = this.gridConfig;

      // Check if we're using 4-point morphing or legacy morphing
      const using4Point = cornerOffsets && (
        cornerOffsets.topLeft.x !== 0 || cornerOffsets.topLeft.y !== 0 ||
        cornerOffsets.topRight.x !== 0 || cornerOffsets.topRight.y !== 0 ||
        cornerOffsets.bottomLeft.x !== 0 || cornerOffsets.bottomLeft.y !== 0 ||
        cornerOffsets.bottomRight.x !== 0 || cornerOffsets.bottomRight.y !== 0
      );

      // If no morphing is applied, return as-is
      if (!using4Point && Math.abs(xMorph) < 0.001 && Math.abs(yMorph) < 0.001) {
        return { x: screenX, y: screenY };
      }

      // Use iterative Newton's method to find the inverse
      let x = screenX;
      let y = screenY;

      for (let i = 0; i < 10; i++) {
        const morphed = this.applyMorph(x, y, gridWidth, gridHeight, startX, startY);
        const errorX = morphed.x - screenX;
        const errorY = morphed.y - screenY;

        // If error is small enough, we're done
        if (Math.abs(errorX) < 0.1 && Math.abs(errorY) < 0.1) {
          break;
        }

        x -= errorX;
        y -= errorY;
      }

      return { x, y };
    },

    drawMovementArea() {
      const scaledConfig = this.scaledGridConfig;
      console.log(`[DrawMovement] Called - scaledGridConfig: ${!!scaledConfig}, reachableCells.size: ${this.reachableCells.size}`);

      if (!scaledConfig || this.reachableCells.size === 0) {
        console.warn('[DrawMovement] ⚠️ Returning early - no grid config or no reachable cells');
        return;
      }

      console.log('[DrawMovement] Drawing movement area for', this.reachableCells.size, 'cells');
      const { cellWidth, cellHeight, columns, rows } = scaledConfig;

      // Position movement area relative to the image, not the canvas
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      // Check if only starting cell is reachable (no movement possible)
      const hasMovementOptions = this.reachableCells.size > 1;

      this.reachableCells.forEach((cellInfo, key) => {
        const x = startX + cellInfo.x * cellWidth;
        const y = startY + cellInfo.y * cellHeight;

        const topLeft = this.applyMorph(x, y, gridWidth, gridHeight, startX, startY);
        const topRight = this.applyMorph(x + cellWidth, y, gridWidth, gridHeight, startX, startY);
        const bottomLeft = this.applyMorph(x, y + cellHeight, gridWidth, gridHeight, startX, startY);
        const bottomRight = this.applyMorph(x + cellWidth, y + cellHeight, gridWidth, gridHeight, startX, startY);

        // If this is the starting cell (cost === 0)
        if (cellInfo.cost === 0) {
          // Only draw starting cell if there are NO other reachable cells (no movement possible)
          if (!hasMovementOptions) {
            // Cyberpunk style: ultra-lightweight semi-transparent background with subtle glowing border
            this.ctx.fillStyle = 'rgba(255, 100, 0, 0.06)';
            this.ctx.beginPath();
            this.ctx.moveTo(topLeft.x, topLeft.y);
            this.ctx.lineTo(topRight.x, topRight.y);
            this.ctx.lineTo(bottomRight.x, bottomRight.y);
            this.ctx.lineTo(bottomLeft.x, bottomLeft.y);
            this.ctx.closePath();
            this.ctx.fill();

            // Subtle glowing border (cyberpunk style)
            this.ctx.strokeStyle = 'rgba(255, 150, 50, 0.5)';
            this.ctx.lineWidth = 1.5;
            this.ctx.shadowBlur = 4;
            this.ctx.shadowColor = 'rgba(255, 150, 50, 0.3)';
            this.ctx.stroke();
            this.ctx.shadowBlur = 0;

            console.warn('[DrawMovement] ⚠️ Player selected but has NO movement options! Check map edges.');
          }
          return; // Skip starting cell for normal movement area
        }

        // Cyberpunk style: ultra-lightweight semi-transparent background with subtle glowing cyan border
        this.ctx.fillStyle = 'rgba(6, 14, 24, 0.12)';
        this.ctx.beginPath();
        this.ctx.moveTo(topLeft.x, topLeft.y);
        this.ctx.lineTo(topRight.x, topRight.y);
        this.ctx.lineTo(bottomRight.x, bottomRight.y);
        this.ctx.lineTo(bottomLeft.x, bottomLeft.y);
        this.ctx.closePath();
        this.ctx.fill();

        // Subtle glowing cyan border for reachable cells
        this.ctx.strokeStyle = 'rgba(99, 247, 255, 0.45)';
        this.ctx.lineWidth = 1.5;
        this.ctx.shadowBlur = 4;
        this.ctx.shadowColor = 'rgba(34, 211, 238, 0.25)';
        this.ctx.stroke();
        this.ctx.shadowBlur = 0;
      });
    },

    drawGrid() {
      const scaledConfig = this.scaledGridConfig;
      if (!scaledConfig) return;

      const { cellWidth, cellHeight, columns, rows } = scaledConfig;

      // Position grid relative to the image, not the canvas
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      this.ctx.strokeStyle = 'rgba(100, 100, 150, 0.3)';
      this.ctx.lineWidth = 1;

      // Draw grid only for passable cells
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
          // Check if this cell is passable
          if (this.isCellPassable(col, row)) {
            const x = startX + col * cellWidth;
            const y = startY + row * cellHeight;

            const topLeft = this.applyMorph(x, y, gridWidth, gridHeight, startX, startY);
            const topRight = this.applyMorph(x + cellWidth, y, gridWidth, gridHeight, startX, startY);
            const bottomLeft = this.applyMorph(x, y + cellHeight, gridWidth, gridHeight, startX, startY);
            const bottomRight = this.applyMorph(x + cellWidth, y + cellHeight, gridWidth, gridHeight, startX, startY);

            this.ctx.beginPath();
            this.ctx.moveTo(topLeft.x, topLeft.y);
            this.ctx.lineTo(topRight.x, topRight.y);
            this.ctx.lineTo(bottomRight.x, bottomRight.y);
            this.ctx.lineTo(bottomLeft.x, bottomLeft.y);
            this.ctx.closePath();
            this.ctx.stroke();
          }
        }
      }
    },

    isCellPassable(x, y) {
      if (!this.currentMap || !this.currentMap.cells) return true; // Default to passable

      const cell = this.currentMap.cells.find(c =>
        c.x === x && c.y === y && c.layer === this.currentLayer
      );

      // If cell doesn't exist in the cells array, it's passable by default
      // If cell exists, check its passable property (default to true if not explicitly false)
      return !cell || cell.passable !== false;
    },

    drawPath(path) {
      const scaledConfig = this.scaledGridConfig;
      if (!scaledConfig || path.length < 2) return;

      const { cellWidth, cellHeight, columns, rows } = scaledConfig;

      // Position path relative to the image, not the canvas
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      // First, highlight individual cells along the path
      path.forEach((cell, index) => {
        if (index === 0) return; // Skip starting cell (player's current position)

        const x = startX + cell.x * cellWidth;
        const y = startY + cell.y * cellHeight;

        const topLeft = this.applyMorph(x, y, gridWidth, gridHeight, startX, startY);
        const topRight = this.applyMorph(x + cellWidth, y, gridWidth, gridHeight, startX, startY);
        const bottomLeft = this.applyMorph(x, y + cellHeight, gridWidth, gridHeight, startX, startY);
        const bottomRight = this.applyMorph(x + cellWidth, y + cellHeight, gridWidth, gridHeight, startX, startY);

        // Cyberpunk style: ultra-lightweight semi-transparent background for path cells
        this.ctx.fillStyle = 'rgba(255, 200, 0, 0.05)';
        this.ctx.beginPath();
        this.ctx.moveTo(topLeft.x, topLeft.y);
        this.ctx.lineTo(topRight.x, topRight.y);
        this.ctx.lineTo(bottomRight.x, bottomRight.y);
        this.ctx.lineTo(bottomLeft.x, bottomLeft.y);
        this.ctx.closePath();
        this.ctx.fill();

        // Subtle glowing yellow border for path cells
        this.ctx.strokeStyle = 'rgba(255, 220, 100, 0.55)';
        this.ctx.lineWidth = 1.5;
        this.ctx.shadowBlur = 5;
        this.ctx.shadowColor = 'rgba(255, 220, 100, 0.3)';
        this.ctx.stroke();
        this.ctx.shadowBlur = 0;
      });

      // Then, draw the connecting line through cell centers with subtle cyberpunk glow
      this.ctx.strokeStyle = 'rgba(255, 220, 100, 0.6)';
      this.ctx.lineWidth = 2;
      this.ctx.setLineDash([8, 4]);
      this.ctx.shadowBlur = 6;
      this.ctx.shadowColor = 'rgba(255, 220, 100, 0.4)';

      this.ctx.beginPath();
      path.forEach((cell, index) => {
        const centerX = startX + cell.x * cellWidth + cellWidth / 2;
        const centerY = startY + cell.y * cellHeight + cellHeight / 2;
        const point = this.applyMorph(centerX, centerY, gridWidth, gridHeight, startX, startY);

        if (index === 0) {
          this.ctx.moveTo(point.x, point.y);
        } else {
          this.ctx.lineTo(point.x, point.y);
        }
      });
      this.ctx.stroke();
      this.ctx.setLineDash([]);
      this.ctx.shadowBlur = 0;
    },

    drawCellShadow(x, y) {
      const scaledConfig = this.scaledGridConfig;
      if (!scaledConfig) return;

      const { cellWidth, cellHeight, columns, rows } = scaledConfig;

      // Position cell shadow relative to the image, not the canvas
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      const cellX = startX + x * cellWidth;
      const cellY = startY + y * cellHeight;

      const topLeft = this.applyMorph(cellX, cellY, gridWidth, gridHeight, startX, startY);
      const topRight = this.applyMorph(cellX + cellWidth, cellY, gridWidth, gridHeight, startX, startY);
      const bottomLeft = this.applyMorph(cellX, cellY + cellHeight, gridWidth, gridHeight, startX, startY);
      const bottomRight = this.applyMorph(cellX + cellWidth, cellY + cellHeight, gridWidth, gridHeight, startX, startY);

      // Cyberpunk style: ultra-lightweight semi-transparent background with subtle glowing border for hover
      this.ctx.fillStyle = 'rgba(99, 247, 255, 0.04)';
      this.ctx.beginPath();
      this.ctx.moveTo(topLeft.x, topLeft.y);
      this.ctx.lineTo(topRight.x, topRight.y);
      this.ctx.lineTo(bottomRight.x, bottomRight.y);
      this.ctx.lineTo(bottomLeft.x, bottomLeft.y);
      this.ctx.closePath();
      this.ctx.fill();

      // Subtle glowing cyan border for hover
      this.ctx.strokeStyle = 'rgba(99, 247, 255, 0.4)';
      this.ctx.lineWidth = 1.5;
      this.ctx.shadowBlur = 6;
      this.ctx.shadowColor = 'rgba(34, 211, 238, 0.35)';
      this.ctx.stroke();
      this.ctx.shadowBlur = 0;
    },

    // DEPRECATED: Players are now rendered as DOM elements using ObjectToken components
    // See tokens-overlay in template and getTokenStyle method
    /*
    drawPlayers() {
      if (!this.gridConfig || !this.players) return;

      const { cellWidth, cellHeight, columns, rows } = this.gridConfig;
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = (this.canvasWidth - gridWidth) / 2 + this.offsetX;
      const startY = (this.canvasHeight - gridHeight) / 2 + this.offsetY;

      this.players.forEach(player => {
        const centerX = startX + player.x * cellWidth + cellWidth / 2;
        const centerY = startY + player.y * cellHeight + cellHeight / 2;
        const point = this.applyMorph(centerX, centerY, gridWidth, gridHeight, startX, startY);

        // Draw player token circle
        const radius = Math.min(cellWidth, cellHeight) / 3;

        // Highlight selected player
        if (this.selectedPlayer && this.selectedPlayer.id === player.id) {
          this.ctx.strokeStyle = 'rgba(255, 255, 0, 0.8)';
          this.ctx.lineWidth = 3;
          this.ctx.beginPath();
          this.ctx.arc(point.x, point.y, radius + 5, 0, Math.PI * 2);
          this.ctx.stroke();
        }

        // Draw player circle
        this.ctx.fillStyle = '#4682B4';
        this.ctx.strokeStyle = '#000';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.stroke();

        // Draw player name
        this.ctx.fillStyle = '#fff';
        this.ctx.font = '12px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(player.name.substring(0, 3), point.x, point.y);

        // Draw action points
        this.ctx.fillStyle = '#ffff00';
        this.ctx.font = '10px Arial';
        this.ctx.fillText(`${player.currentActionPoints}/${player.maxActionPoints}`, point.x, point.y + radius + 12);
      });
    },
    */

    getCellFromMouse(mouseX, mouseY) {
      const scaledConfig = this.scaledGridConfig;
      if (!scaledConfig) return null;

      // Apply inverse zoom transformation to mouse coordinates
      const centerX = this.canvasWidth / 2;
      const centerY = this.canvasHeight / 2;
      const zoomedMouseX = centerX + (mouseX - centerX) / this.zoom;
      const zoomedMouseY = centerY + (mouseY - centerY) / this.zoom;

      const { cellWidth, cellHeight, columns, rows } = scaledConfig;

      // Use image position as reference for grid, not canvas dimensions
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      const unmorphed = this.inverseMorph(zoomedMouseX, zoomedMouseY, gridWidth, gridHeight, startX, startY);
      const x = Math.floor((unmorphed.x - startX) / cellWidth);
      const y = Math.floor((unmorphed.y - startY) / cellHeight);

      if (x >= 0 && x < columns && y >= 0 && y < rows) {
        return { x, y, layer: this.currentLayer };
      }

      return null;
    },

    handleMouseDown(event) {
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // Middle mouse button (wheel press) - enter pan mode
      if (event.button === 1) {
        event.preventDefault();
        this.isPanning = true;
        this.panStartX = mouseX;
        this.panStartY = mouseY;
        this.panStartOffsetX = this.offsetX;
        this.panStartOffsetY = this.offsetY;
        this.canvas.style.cursor = 'grabbing';
        return;
      }

      // If a skill is selected, don't handle movement/selection - let handleCanvasClick handle skill casting
      if (this.selectedSkill) {
        return;
      }

      const cell = this.getCellFromMouse(mouseX, mouseY);
      if (!cell) return;

      // Don't allow new actions during animation
      if (this.isAnimating) return;

      // Check if clicking on a player
      const clickedPlayer = this.players.find(p =>
        p.x === cell.x && p.y === cell.y && p.layer === cell.layer
      );

      if (clickedPlayer) {
        // Select the player
        this.selectPlayer(clickedPlayer.id);
      } else if (this.selectedPlayer) {
        // Try to move selected player to this cell
        const key = `${cell.x},${cell.y},${cell.layer}`;
        const reachableCell = this.reachableCells.get(key);

        if (reachableCell) {
          // Animate movement along the path
          this.animateMovement(
            this.selectedPlayer.id,
            reachableCell.path,
            cell.x,
            cell.y,
            cell.layer,
            reachableCell.cost
          );
        }
      }
    },

    handleMouseMove(event) {
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // Handle pan mode
      if (this.isPanning) {
        const deltaX = mouseX - this.panStartX;
        const deltaY = mouseY - this.panStartY;
        this.offsetX = this.panStartOffsetX + deltaX;
        this.offsetY = this.panStartOffsetY + deltaY;
        this.render();
        return;
      }

      const cell = this.getCellFromMouse(mouseX, mouseY);

      if (cell) {
        this.hoveredCell = cell;

        // If a skill is selected, calculate affected cells for preview
        if (this.selectedSkill && this.selectedPlayer) {
          this.skillTargetPosition = { x: cell.x, y: cell.y };
          this.calculateSkillAffectedCells(cell.x, cell.y);
        } else if (this.selectedPlayer) {
          // Calculate path to hovered cell if it's reachable (normal movement mode)
          const key = `${cell.x},${cell.y},${cell.layer}`;
          const reachableCell = this.reachableCells.get(key);

          if (reachableCell) {
            this.pathToHovered = reachableCell.path;
          } else {
            this.pathToHovered = null;
          }
        }
      } else {
        this.hoveredCell = null;
        this.pathToHovered = null;
        if (this.selectedSkill) {
          this.skillTargetPosition = null;
          this.skillAffectedCells = [];
          this.skillTargetDistance = null;
        }
      }

      this.render();
    },

    handleMouseUp(event) {
      // Exit pan mode if middle button was released
      if (this.isPanning && event.button === 1) {
        this.isPanning = false;
        this.canvas.style.cursor = 'pointer';
      }
    },

    handleMouseLeave() {
      // Exit pan mode if active
      if (this.isPanning) {
        this.isPanning = false;
        this.canvas.style.cursor = 'pointer';
      }

      this.hoveredCell = null;
      this.pathToHovered = null;
      this.render();
    },

    // Calculate token position with morph transformation applied
    getTokenStyle(player) {
      const scaledConfig = this.scaledGridConfig;
      if (!scaledConfig) return { display: 'none' };

      const { cellWidth, cellHeight, columns, rows } = scaledConfig;

      // Position tokens relative to the image, not the canvas
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      let playerX = player.x;
      let playerY = player.y;

      // If this player is being animated, use interpolated position
      if (this.isAnimating && this.animationPlayerId === player.id && this.animationPath) {
        const currentStepIndex = this.animationCurrentStep;
        const nextStepIndex = currentStepIndex + 1;

        if (nextStepIndex < this.animationPath.length) {
          const currentCell = this.animationPath[currentStepIndex];
          const nextCell = this.animationPath[nextStepIndex];

          // Interpolate between current and next cell
          playerX = currentCell.x + (nextCell.x - currentCell.x) * this.animationProgress;
          playerY = currentCell.y + (nextCell.y - currentCell.y) * this.animationProgress;
        } else {
          // Animation complete, use final position
          const finalCell = this.animationPath[this.animationPath.length - 1];
          playerX = finalCell.x;
          playerY = finalCell.y;
        }
      }

      // Calculate center of the cell
      const centerX = startX + playerX * cellWidth + cellWidth / 2;
      const centerY = startY + playerY * cellHeight + cellHeight / 2;

      // Apply morph transformation
      const morphedPoint = this.applyMorph(centerX, centerY, gridWidth, gridHeight, startX, startY);

      // Token size - use the smaller dimension to ensure it fits in the cell
      const tokenSize = Math.min(cellWidth, cellHeight) * 1.3;

      return {
        position: 'absolute',
        left: `${morphedPoint.x}px`,
        top: `${morphedPoint.y}px`,
        width: `${tokenSize}px`,
        height: `${tokenSize}px`,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'auto',
        cursor: 'pointer',
        transition: this.isAnimating && this.animationPlayerId === player.id ? 'none' : 'all 0.3s ease'
      };
    },

    // Calculate item token position (smaller than character tokens for visual distinction)
    getItemTokenStyle(item) {
      const scaledConfig = this.scaledGridConfig;
      if (!scaledConfig) return { display: 'none' };

      const { cellWidth, cellHeight, columns, rows } = scaledConfig;

      // Position tokens relative to the image, not the canvas
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cellWidth;
      const gridHeight = rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      const itemX = item.x;
      const itemY = item.y;

      // Calculate center of the cell
      const centerX = startX + itemX * cellWidth + cellWidth / 2;
      const centerY = startY + itemY * cellHeight + cellHeight / 2;

      // Apply morph transformation
      const morphedPoint = this.applyMorph(centerX, centerY, gridWidth, gridHeight, startX, startY);

      // Item token size - SMALLER than character tokens (0.7x instead of 1.3x)
      // This makes it easy for players to distinguish items from characters
      const tokenSize = Math.min(cellWidth, cellHeight) * 0.7;

      return {
        position: 'absolute',
        left: `${morphedPoint.x}px`,
        top: `${morphedPoint.y}px`,
        width: `${tokenSize}px`,
        height: `${tokenSize}px`,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'auto',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      };
    },

    // Check if a player is knocked out - uses the 'alive' property from backend
    // This matches the logic used in CharacterCardHolder (:dead="!character.alive")
    isPlayerKnockedOut(player) {
      // Return the opposite of the 'alive' property
      // If alive is not set, default to false (not knocked out)
      return player.alive === false;
    },

    // Handle clicking on a token
    handleTokenClick(playerId) {
      this.selectPlayer(playerId);
    },

    // Handle right-click on a token to show context menu
    handleTokenContextMenu(event, player) {
      event.stopPropagation();
      this.contextMenuCharacter = player;
      this.contextMenuPosition = {
        x: event.clientX,
        y: event.clientY
      };
      this.contextMenuVisible = true;
    },

    // Handle clicking on an item token
    handleItemTokenClick(itemId) {
      console.log('[Item Token] Clicked item:', itemId);
      // TODO: Implement item interaction (pickup, inspect, use, etc.)
      // For now, just log the interaction
    },

    // Handle right-click on an item token
    handleItemTokenContextMenu(event, item) {
      event.stopPropagation();
      console.log('[Item Token] Right-clicked item:', item);
      // TODO: Show item-specific context menu (pickup, inspect, etc.)
      // For now, prevent default context menu
    },

    // Close context menu
    closeContextMenu() {
      this.contextMenuVisible = false;
      this.contextMenuCharacter = null;
    },

    // Easing function for smooth animation (ease-in-out)
    easeInOutQuad(t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    },

    // Animate smooth movement along a path
    animateMovement(playerId, path, destinationX, destinationY, destinationLayer, movementCost) {
      if (!path || path.length < 2) {
        // No path or too short, just move instantly
        this.movePlayer({
          playerId,
          x: destinationX,
          y: destinationY,
          layer: destinationLayer,
          cost: movementCost
        });
        return;
      }

      // Set up animation state
      this.isAnimating = true;
      this.animationPlayerId = playerId;
      this.animationPath = path;
      this.animationCurrentStep = 0;
      this.animationProgress = 0;

      const stepDuration = 250; // milliseconds per cell (slightly faster for smoother feel)
      let lastTimestamp = performance.now();

      const animate = (timestamp) => {
        if (!this.isAnimating) return; // Animation was cancelled

        const deltaTime = timestamp - lastTimestamp;
        lastTimestamp = timestamp;

        // Update progress within current step
        const rawProgress = this.animationProgress + deltaTime / stepDuration;

        if (rawProgress >= 1.0) {
          // Move to next step
          this.animationCurrentStep++;
          this.animationProgress = 0;

          // Check if we've reached the end of the path
          if (this.animationCurrentStep >= this.animationPath.length - 1) {
            // Animation complete
            this.isAnimating = false;
            this.animationPlayerId = null;
            this.animationPath = null;
            this.animationCurrentStep = 0;
            this.animationProgress = 0;

            // Update player position in Vuex store
            this.movePlayer({
              playerId,
              x: destinationX,
              y: destinationY,
              layer: destinationLayer,
              cost: movementCost
            });

            // Re-render to show final position
            this.render();
            return;
          }
        } else {
          // Apply easing to progress for smoother movement
          this.animationProgress = rawProgress;
        }

        // Trigger re-render to update token position
        this.$forceUpdate();

        // Continue animation
        requestAnimationFrame(animate);
      };

      // Start animation loop
      requestAnimationFrame(animate);
    },

    // ========== SKILL RENDERING METHODS ==========

    drawSkillPreview() {
      if (!this.selectedSkill || !this.selectedPlayer || !this.skillTargetPosition) {
        return;
      }

      const scaledConfig = this.scaledGridConfig;
      if (!scaledConfig) return;

      const { cellWidth, cellHeight } = scaledConfig;
      const imagePos = this.getImagePosition();
      const gridWidth = scaledConfig.columns * cellWidth;
      const gridHeight = scaledConfig.rows * cellHeight;
      const startX = imagePos.x;
      const startY = imagePos.y;

      // Draw line from caster to target anchor point
      this.drawSkillTraceLine(startX, startY, gridWidth, gridHeight, cellWidth, cellHeight);

      // Draw affected cells with color coding
      this.skillAffectedCells.forEach(cell => {
        const centerX = startX + cell.x * cellWidth + cellWidth / 2;
        const centerY = startY + cell.y * cellHeight + cellHeight / 2;
        const morphedPoint = this.applyMorph(centerX, centerY, gridWidth, gridHeight, startX, startY);

        // Determine color and opacity based on validity and target type
        let fillColor;
        let strokeColor;
        let alpha = 0.4;

        if (!cell.valid) {
          // Out of range - show as invalid (darker red)
          fillColor = 'rgba(255, 0, 0, 0.2)';
          strokeColor = 'rgba(255, 0, 0, 0.6)';
        } else {
          // In range - use color coding
          switch (cell.color) {
            case 'red': // Enemy target
              fillColor = 'rgba(255, 50, 50, 0.5)';
              strokeColor = 'rgba(255, 0, 0, 0.8)';
              alpha = 0.5;
              break;
            case 'green': // Ally target
              fillColor = 'rgba(50, 255, 50, 0.5)';
              strokeColor = 'rgba(0, 255, 0, 0.8)';
              alpha = 0.5;
              break;
            case 'gold': // Empty/neutral area
            default:
              fillColor = 'rgba(255, 215, 0, 0.4)';
              strokeColor = 'rgba(255, 215, 0, 0.8)';
              alpha = 0.4;
              break;
          }
        }

        // Draw cell highlight
        this.ctx.save();
        this.ctx.globalAlpha = alpha;
        this.ctx.fillStyle = fillColor;
        this.ctx.strokeStyle = strokeColor;
        this.ctx.lineWidth = 2;

        // Draw rhombus/diamond shape for the cell
        this.ctx.beginPath();
        this.ctx.moveTo(morphedPoint.x, morphedPoint.y - cellHeight / 2);
        this.ctx.lineTo(morphedPoint.x + cellWidth / 2, morphedPoint.y);
        this.ctx.lineTo(morphedPoint.x, morphedPoint.y + cellHeight / 2);
        this.ctx.lineTo(morphedPoint.x - cellWidth / 2, morphedPoint.y);
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.stroke();

        this.ctx.restore();
      });

      // Draw target anchor point indicator
      const targetCenterX = startX + this.skillTargetPosition.x * cellWidth + cellWidth / 2;
      const targetCenterY = startY + this.skillTargetPosition.y * cellHeight + cellHeight / 2;
      const targetMorphed = this.applyMorph(targetCenterX, targetCenterY, gridWidth, gridHeight, startX, startY);

      this.ctx.save();
      this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      this.ctx.lineWidth = 3;
      this.ctx.beginPath();
      this.ctx.arc(targetMorphed.x, targetMorphed.y, 8, 0, Math.PI * 2);
      this.ctx.stroke();
      this.ctx.restore();
    },

    drawSkillTraceLine(startX, startY, gridWidth, gridHeight, cellWidth, cellHeight) {
      if (!this.selectedPlayer || !this.skillTargetPosition) return;

      // Calculate caster position
      const casterCenterX = startX + this.selectedPlayer.x * cellWidth + cellWidth / 2;
      const casterCenterY = startY + this.selectedPlayer.y * cellHeight + cellHeight / 2;
      const casterMorphed = this.applyMorph(casterCenterX, casterCenterY, gridWidth, gridHeight, startX, startY);

      // Calculate target position
      const targetCenterX = startX + this.skillTargetPosition.x * cellWidth + cellWidth / 2;
      const targetCenterY = startY + this.skillTargetPosition.y * cellHeight + cellHeight / 2;
      const targetMorphed = this.applyMorph(targetCenterX, targetCenterY, gridWidth, gridHeight, startX, startY);

      // Determine line color based on range validity
      const distance = this.skillTargetDistance;
      const minRange = this.selectedSkill.min_range || 0;
      const maxRange = this.selectedSkill.max_range || Infinity;
      const isInRange = distance >= minRange && distance <= maxRange;

      const lineColor = isInRange ? 'rgba(0, 212, 255, 0.7)' : 'rgba(255, 0, 0, 0.7)';

      // Draw line from caster to target
      this.ctx.save();
      this.ctx.strokeStyle = lineColor;
      this.ctx.lineWidth = 3;
      this.ctx.setLineDash([10, 5]);
      this.ctx.beginPath();
      this.ctx.moveTo(casterMorphed.x, casterMorphed.y);
      this.ctx.lineTo(targetMorphed.x, targetMorphed.y);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
      this.ctx.restore();

      // Draw arrow head at target
      const angle = Math.atan2(targetMorphed.y - casterMorphed.y, targetMorphed.x - casterMorphed.x);
      const arrowSize = 12;

      this.ctx.save();
      this.ctx.fillStyle = lineColor;
      this.ctx.beginPath();
      this.ctx.moveTo(targetMorphed.x, targetMorphed.y);
      this.ctx.lineTo(
        targetMorphed.x - arrowSize * Math.cos(angle - Math.PI / 6),
        targetMorphed.y - arrowSize * Math.sin(angle - Math.PI / 6)
      );
      this.ctx.lineTo(
        targetMorphed.x - arrowSize * Math.cos(angle + Math.PI / 6),
        targetMorphed.y - arrowSize * Math.sin(angle + Math.PI / 6)
      );
      this.ctx.closePath();
      this.ctx.fill();
      this.ctx.restore();
    },

    // ========== CHARACTER DATA LOADING ==========

    loadPlayerSkillsAndItems(player) {
      if (!player) {
        this.playerSkills = [];
        this.playerItems = [];
        return;
      }

      console.log('[PlayerCanvas] Loading skills and items for:', player.name);
      console.log('[PlayerCanvas] Character data:', player.characterData);

      // Load skills - ActionHolder expects array of skill objects
      if (player.characterData?.skills && Array.isArray(player.characterData.skills)) {
        this.playerSkills = player.characterData.skills;
        console.log('[PlayerCanvas] ✅ Loaded', this.playerSkills.length, 'skills');
      } else if (player.skills && Array.isArray(player.skills)) {
        this.playerSkills = player.skills;
        console.log('[PlayerCanvas] ✅ Loaded', this.playerSkills.length, 'skills from player.skills');
      } else {
        this.playerSkills = [];
        console.warn('[PlayerCanvas] ⚠️ No skills found');
      }

      // Load items - ActionHolder expects array of WorldItem objects
      if (player.characterData?.items && Array.isArray(player.characterData.items)) {
        this.playerItems = player.characterData.items;
        console.log('[PlayerCanvas] ✅ Loaded', this.playerItems.length, 'items');
      } else if (player.items && Array.isArray(player.items)) {
        this.playerItems = player.items;
        console.log('[PlayerCanvas] ✅ Loaded', this.playerItems.length, 'items from player.items');
      } else {
        this.playerItems = [];
        console.warn('[PlayerCanvas] ⚠️ No items found');
      }
    },

    // ========== SKILL TARGETING METHODS ==========

    handleSkillSelected(skill) {
      this.selectSkill(skill);
    },

    handleItemSelected(item) {
      console.log('[Item] Selected item:', item);
      // TODO: Handle item selection (items with skills should work like skills)
      if (item.item && item.item.skill) {
        this.selectSkill(item.item.skill);
      }
    },

    selectSkill(skill) {
      this.selectedSkill = skill;
      this.skillRotation = 0;
      this.skillTargetPosition = null;
      this.skillAffectedCells = [];
      this.skillTargetDistance = null;
      console.log('[Skill] Selected skill:', skill.name);
    },

    cancelSkillSelection() {
      this.selectedSkill = null;
      this.skillRotation = 0;
      this.skillTargetPosition = null;
      this.skillAffectedCells = [];
      this.skillTargetDistance = null;
      this.render();
      console.log('[Skill] Cancelled skill selection');
    },

    rotateSkillPattern(degrees) {
      this.skillRotation = (this.skillRotation + degrees + 360) % 360;
      if (this.skillTargetPosition) {
        this.calculateSkillAffectedCells(this.skillTargetPosition.x, this.skillTargetPosition.y);
      }
      this.render();
      console.log('[Skill] Rotated to:', this.skillRotation);
    },

    handleCanvasClick(event) {
      if (this.selectedSkill && this.skillTargetPosition) {
        // Cast the skill at the current target position
        this.castSkill();
      }
    },

    castSkill() {
      if (!this.selectedSkill || !this.skillTargetPosition || !this.selectedPlayer) {
        return;
      }

      // Check if target is in valid range
      const distance = this.calculateGridDistance(
        this.selectedPlayer.x,
        this.selectedPlayer.y,
        this.skillTargetPosition.x,
        this.skillTargetPosition.y
      );

      const minRange = this.selectedSkill.min_range || 0;
      const maxRange = this.selectedSkill.max_range || Infinity;

      if (distance < minRange || distance > maxRange) {
        console.log('[Skill] Target out of range');
        return;
      }

      console.log('[Skill] Casting skill:', this.selectedSkill.name, 'at', this.skillTargetPosition);
      console.log('[Skill] Affected cells:', this.skillAffectedCells);
      console.log('[Skill] Rotation:', this.skillRotation);

      // TODO: Emit event or call API to execute skill
      // this.$emit('skill-cast', {
      //   skill: this.selectedSkill,
      //   caster: this.selectedPlayer,
      //   targetPosition: this.skillTargetPosition,
      //   rotation: this.skillRotation,
      //   affectedCells: this.skillAffectedCells
      // });

      // Clear skill selection after casting
      this.cancelSkillSelection();
    },

    calculateGridDistance(x1, y1, x2, y2) {
      // Chebyshev distance (grid with diagonal movement)
      const dx = Math.abs(x1 - x2);
      const dy = Math.abs(y1 - y2);
      return Math.max(dx, dy);
    },

    calculateSkillAffectedCells(targetX, targetY) {
      if (!this.selectedSkill || !this.selectedPlayer) {
        this.skillAffectedCells = [];
        this.skillTargetDistance = null;
        return;
      }

      const sprayConfig = this.selectedSkill.spray_config;
      if (!sprayConfig || !sprayConfig.spray) {
        this.skillAffectedCells = [];
        this.skillTargetDistance = null;
        return;
      }

      // Calculate distance from caster to target
      const distance = this.calculateGridDistance(
        this.selectedPlayer.x,
        this.selectedPlayer.y,
        targetX,
        targetY
      );
      this.skillTargetDistance = distance;

      const minRange = this.selectedSkill.min_range || 0;
      const maxRange = this.selectedSkill.max_range || Infinity;
      const isInRange = distance >= minRange && distance <= maxRange;

      // Find alignment point in spray pattern
      const spray = sprayConfig.spray;
      let alignmentX = null;
      let alignmentY = null;

      for (let row = 0; row < spray.length; row++) {
        for (let col = 0; col < spray[row].length; col++) {
          if (spray[row][col] === 'x') {
            alignmentX = col;
            alignmentY = row;
            break;
          }
        }
        if (alignmentX !== null) break;
      }

      if (alignmentX === null) {
        console.warn('[Skill] No alignment point found in spray pattern');
        this.skillAffectedCells = [];
        return;
      }

      // Rotate the spray pattern
      const rotatedSpray = this.rotateMatrix(spray, this.skillRotation);

      // Update alignment point after rotation
      let rotatedAlignX = alignmentX;
      let rotatedAlignY = alignmentY;

      if (this.skillRotation === 90) {
        rotatedAlignX = alignmentY;
        rotatedAlignY = spray[0].length - 1 - alignmentX;
      } else if (this.skillRotation === 180) {
        rotatedAlignX = spray[0].length - 1 - alignmentX;
        rotatedAlignY = spray.length - 1 - alignmentY;
      } else if (this.skillRotation === 270) {
        rotatedAlignX = spray.length - 1 - alignmentY;
        rotatedAlignY = alignmentX;
      }

      // Calculate affected cells
      const affected = [];
      for (let row = 0; row < rotatedSpray.length; row++) {
        for (let col = 0; col < rotatedSpray[row].length; col++) {
          if (rotatedSpray[row][col] === 1 || rotatedSpray[row][col] === 'x') {
            const offsetX = col - rotatedAlignX;
            const offsetY = row - rotatedAlignY;
            const cellX = targetX + offsetX;
            const cellY = targetY + offsetY;

            // Determine cell validity and color
            let valid = isInRange;
            let color = 'gold'; // Default color for valid skill area

            // Check if there are enemies/allies at this position
            const objectAtCell = this.getObjectAtCell(cellX, cellY);
            if (objectAtCell) {
              // Red for enemies, green for allies, gold for neutral/empty
              if (this.isEnemy(objectAtCell)) {
                color = 'red';
              } else if (this.isAlly(objectAtCell)) {
                color = 'green';
              }
            }

            affected.push({ x: cellX, y: cellY, valid, color });
          }
        }
      }

      this.skillAffectedCells = affected;
    },

    rotateMatrix(matrix, degrees) {
      if (degrees === 0) {
        return matrix;
      }

      let result = matrix.map(row => [...row]);

      if (degrees === 90) {
        // Transpose then reverse each row
        result = result[0].map((_, colIndex) => result.map(row => row[colIndex]));
        result = result.map(row => row.reverse());
      } else if (degrees === 180) {
        // Reverse rows, then reverse each row
        result = result.reverse().map(row => row.reverse());
      } else if (degrees === 270) {
        // Reverse each row, then transpose
        result = result.map(row => row.reverse());
        result = result[0].map((_, colIndex) => result.map(row => row[colIndex]));
      }

      return result;
    },

    getObjectAtCell(x, y) {
      // Check if any player/NPC is at this cell
      return this.players.find(p => p.x === x && p.y === y && p.layer === this.currentLayer);
    },

    isEnemy(gameObject) {
      // TODO: Implement proper enemy detection based on game logic
      // For now, assume NPCs are enemies and players are allies
      return gameObject.type === 'npc' || gameObject.isEnemy;
    },

    isAlly(gameObject) {
      // TODO: Implement proper ally detection based on game logic
      return gameObject.type === 'player' || !gameObject.isEnemy;
    }
  }
};
</script>

<style scoped>
.player-canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.player-canvas {
  width: 100%;
  height: 100%;
  cursor: pointer;
}

/* Same contract as the player board's own effects layer: laid over the floor at its
   own size, never CSS-scaled by the zoom - the zoom is folded into the four grid
   corners handed to the shader, so a cell drawn here and a cell painted by the 2D
   canvas above it are one function of one set of numbers. */
.impact-stage {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.coordinate-display {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  background: var(--gm-surface-1);
  border: 1px solid var(--gm-border);
  border-radius: var(--gm-radius-sm);
  padding: 0.5rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  pointer-events: none;
  box-shadow: var(--gm-shadow);
}

.coord-label {
  font-family: var(--gm-font-display);
  font-size: 0.65rem;
  color: var(--gm-accent);
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.coord-value {
  font-size: 0.8rem;
  color: var(--gm-text);
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

.coord-layer {
  font-size: 0.65rem;
  color: var(--gm-text-muted);
  font-style: italic;
}

.coord-distance {
  font-size: 0.7rem;
  color: var(--gm-accent-hi);
  font-weight: 600;
  margin-top: 0.25rem;
  padding-top: 0.25rem;
  border-top: 1px solid var(--gm-border-dim);
}

.range-indicator {
  font-size: 0.6rem;
  color: var(--gm-text-muted);
  margin-left: 0.25rem;
}

/* Tokens overlay - positioned on top of canvas */
.tokens-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

/* Individual token wrapper */
.token-wrapper {
  position: absolute;
  pointer-events: auto;
  transition: transform 0.3s ease, filter 0.2s ease;
  z-index: 10;
}

/* Disable pointer events when skill is selected to allow targeting through tokens */
.token-no-pointer {
  pointer-events: none !important;
}

.token-wrapper:hover {
  transform: translate(-50%, -50%) scale(1.1);
  filter: drop-shadow(0 0 0.5rem rgba(250, 218, 149, 0.6));
}

/* Selected token highlight */
.token-selected {
  filter: drop-shadow(0 0 0.15rem rgba(255, 255, 0, 0.8));
  z-index: 11;
}

.token-selected:hover {
  transform: translate(-50%, -50%) scale(1.1);
  filter: drop-shadow(0 0 0.2rem rgba(255, 255, 0, 0.8));
}

/* Token name label */
.token-name {
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 6px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.75);
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  border: 1px solid var(--gm-border-dim);
}

/* Item token styling - visually distinct from character tokens */
.item-token {
  opacity: 0.9;
  z-index: 5; /* Lower z-index than character tokens */
}

.item-token:hover {
  transform: translate(-50%, -50%) scale(1.15);
  filter: drop-shadow(0 0 0.4rem rgba(255, 215, 0, 0.6));
  opacity: 1;
}

/* Item name label - different color scheme */
.item-name {
  background: rgba(20, 20, 20, 0.85);
  border: 1px solid var(--gm-border-hi);
  color: var(--gm-accent-hi);
  font-size: 0.3rem;
}

/* Zoom HUD */
.zoom-hud {
  position: absolute;
  bottom: 1.25rem;
  right: 1.25rem;
  background: var(--gm-surface-1);
  border: 1px solid var(--gm-border);
  border-radius: var(--gm-radius);
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: var(--gm-shadow);
  z-index: 100;
}

.zoom-btn {
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-accent);
  font-size: 1.1rem;
  font-weight: bold;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.zoom-btn:hover:not(:disabled) {
  background: var(--gm-accent-soft);
  border-color: var(--gm-border);
}

.zoom-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.zoom-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.zoom-reset {
  font-size: 1rem;
  margin-left: 0.25rem;
}

.zoom-display {
  color: var(--gm-text);
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  min-width: 3.1rem;
  text-align: center;
  user-select: none;
}

/* Action Holder Panel (Skills & Items) - Match Location.vue styling */
.rpg-action-holder {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

/* Rotation Control */
.rotation-control {
  position: fixed;
  bottom: 16.25rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--gm-surface-1);
  border: 1px solid var(--gm-border-hi);
  border-radius: var(--gm-radius);
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: var(--gm-shadow);
  z-index: 1001;
}

.rotation-btn {
  background: rgba(255, 215, 0, 0.12);
  border: 1px solid var(--gm-border-hi);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-accent-hi);
  font-size: 1.1rem;
  font-weight: bold;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.rotation-btn:hover {
  background: rgba(255, 215, 0, 0.22);
}

.rotation-btn:active {
  transform: scale(0.95);
}

.rotation-display {
  color: var(--gm-accent-hi);
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  min-width: 3.1rem;
  text-align: center;
  user-select: none;
}
</style>
